import { ListOutput, StandardResponse } from '@allocab/allocab-commons-shared-types';
import { AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import type { TranslateOptions } from 'i18n-js';

import type { Leaves, LocalizedDictionary } from '../translations';

export type APIResponseDataType = 'item' | 'list' | undefined;

// Responses
export type APIResponse<T> = StandardResponse<T>;

// Errors
export type APIError = APIServerError | APINetworkError | APICodeError;

export type APIServerError = {
  type: 'server';
  nativeError?: AxiosError;
  statusCode: AxiosResponse['status'];
} & Partial<StandardResponse<{ errorMessage: string; errorsDetails?: Record<string, unknown> }>>;

export type APINetworkError = {
  type: 'network';
  nativeError?: AxiosError;
  message: string;
};

export type APICodeError = {
  type: 'code';
  nativeError?: AxiosError;
  message: string;
};

export const isAPIError = (error: unknown): error is APIError => {
  return ['server', 'network', 'code'].includes((error as APIError)?.type);
};

// Fonction de traduction
export type TranslateFunction = (_key: Leaves<LocalizedDictionary>, _options?: TranslateOptions) => string;

export type Options = Omit<AxiosRequestConfig, 'params'> & {
  skipAuth?: boolean;
};

export interface ApiClient {
  get<Input, Output, Format extends APIResponseDataType = 'item'>(
    _path: string,
    _params?: Input,
    _options?: Options,
  ): Promise<Format extends 'list' ? ListOutput<Output> : Output>;
  post<Input, Output, Format extends APIResponseDataType = 'item'>(
    _path: string,
    _data?: Input,
    _options?: Options,
  ): Promise<Format extends 'list' ? ListOutput<Output> : Output>;
  postFormData<Output, Format extends APIResponseDataType = 'item'>(
    _path: string,
    _data: FormData,
    _options?: Options,
  ): Promise<Format extends 'list' ? ListOutput<Output> : Output>;
  patchFormData<Output, Format extends APIResponseDataType = 'item'>(
    _path: string,
    _data: FormData,
    _options?: Options,
  ): Promise<Format extends 'list' ? ListOutput<Output> : Output>;
  put<Input, Output, Format extends APIResponseDataType = 'item'>(
    _path: string,
    _data: Input,
    _options?: Options,
  ): Promise<Format extends 'list' ? ListOutput<Output> : Output>;
  patch<Input, Output, Format extends APIResponseDataType = 'item'>(
    _path: string,
    _data?: Input,
    _options?: Options,
  ): Promise<Format extends 'list' ? ListOutput<Output> : Output>;
  delete<Input, Output, Format extends APIResponseDataType = 'item'>(
    _path: string,
    _data?: Input,
    _options?: Options,
  ): Promise<Format extends 'list' ? ListOutput<Output> : Output>;
}
